import React from 'react';
import { Card, CardContent, Typography, Box, LinearProgress, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { t } from '../../../utils/textUtils';
import { formatPercentage } from '../utils/formatters';

const getProgressLabel = (pct) => {
  if (pct >= 80) return { label: 'Hampir Selesai', color: '#166534', bg: '#DCFCE7' };
  if (pct >= 50) return { label: 'Sedang Berjalan', color: '#92400E', bg: '#FEF3C7' };
  if (pct >= 20) return { label: 'Awal Pengerjaan', color: '#1D4ED8', bg: '#DBEAFE' };
  return { label: 'Baru Dimulai', color: '#991B1B', bg: '#FEE2E2' };
};

const ProgressOverviewSection = ({ 
  currentPercent, 
  normalizedPercent, 
  percentColor, 
  isProjectMode, 
  isUnitMode 
}) => {
  const percentDisplay = formatPercentage(normalizedPercent);
  const statusInfo = getProgressLabel(normalizedPercent);

  return (
    <Card 
      sx={{ 
        mb: 3, 
        border: '1px solid #E2E8F0', 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden'
      }}
    >
      {/* Colored top accent bar */}
      <Box sx={{ height: 4, bgcolor: percentColor }} />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 2,
              bgcolor: percentColor, opacity: 0.15,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative'
            }}>
              <TrendingUpIcon sx={{ color: percentColor, fontSize: 22, position: 'absolute' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                {t('PROGRESS KESELURUHAN')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                {isProjectMode 
                  ? t('Progress seluruh bangunan dalam project') 
                  : isUnitMode 
                    ? t('Progress seluruh kategori dalam bangunan') 
                    : t('Progress kategori yang dipilih')
                }
              </Typography>
            </Box>
          </Box>
          <Chip
            label={statusInfo.label}
            size="small"
            sx={{ bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 600, fontSize: '0.72rem' }}
          />
        </Box>

        {/* Big percentage display */}
        <Box sx={{ 
          display: 'flex', alignItems: 'center', gap: 3, mb: 3,
          p: 2.5, borderRadius: 2,
          background: `linear-gradient(135deg, ${percentColor}12 0%, ${percentColor}06 100%)`,
          border: `1px solid ${percentColor}30`
        }}>
          <Typography sx={{ 
            fontWeight: 900, 
            color: percentColor,
            fontSize: { xs: '3rem', sm: '3.5rem' },
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums'
          }}>
            {percentDisplay}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>Progress</Typography>
              <Typography variant="caption" sx={{ color: percentColor, fontWeight: 700 }}>{Math.round(normalizedPercent)}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={normalizedPercent} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                backgroundColor: '#E2E8F0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: percentColor,
                  borderRadius: 5,
                  transition: 'width 0.5s ease'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>0%</Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>100%</Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProgressOverviewSection;
